import { CalendarClock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AuditPrepChecklist from "@/components/AuditPrepChecklist";

const NEW_ENTRANT_URL = "https://www.fmcsa.dot.gov/safety/new-entrant-safety-assurance-program";

function auditWindow(issued, passengers) {
  if (!issued) return null;
  const start = new Date(`${issued}T00:00:00`);
  const due = new Date(start);
  if (passengers) due.setDate(due.getDate() + 120);
  else due.setFullYear(due.getFullYear() + 1);
  const today = new Date(new Date().toDateString());
  return { due, left: Math.round((due - today) / 86400000) };
}

const leftText = (d) => (d >= 60 ? `about ${Math.round(d / 30)} months left` : `${d} day${d === 1 ? "" : "s"} left`);

export default function NewEntrantAudit({ profile, api, onChanged }) {
  if (profile.operation_type === "Intrastate") return null;
  const passengers = profile.property_type === "Passengers";
  const w = auditWindow(profile.mc_issued_date, passengers);
  const expired = w && w.left < 0;
  const soon = w && !expired && w.left <= 90;
  const saveDate = async (v) => {
    try {
      await api.put("/profile", { mc_issued_date: v || null });
      onChanged();
    } catch {
      toast.error("Could not save the date.");
    }
  };
  return (
    <div className={`border p-3 ${expired ? "border-[#DC2626] bg-[#DC2626]/10" : soon ? "border-[#F59E0B] bg-[#F59E0B]/10" : "border-[#262626] bg-[#0A0A0A]"}`} data-testid="new-entrant-audit">
      <div className="flex items-center gap-2 font-semibold text-sm">
        <CalendarClock size={15} className={expired ? "text-[#F87171]" : soon ? "text-[#F59E0B]" : "text-[#FFD600]"} /> New Entrant Safety Audit
      </div>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        FMCSA audits new carriers within {passengers ? "120 days" : "12 months"} of getting authority. They review driver files, drug testing, hours of service, vehicle maintenance and insurance.
      </p>
      {w ? (
        <p className={`text-sm font-semibold mt-2 ${w.left < 0 ? "text-[#F87171]" : soon ? "text-[#F59E0B]" : "text-[#FFD600]"}`} data-testid="new-entrant-due">
          {w.left >= 0
            ? `Expect your audit by ${w.due.toLocaleDateString()} (${leftText(w.left)})`
            : `${passengers ? "120-day" : "12-month"} window ended ${w.due.toLocaleDateString()}. If you haven't been audited yet, contact FMCSA.`}
        </p>
      ) : (
        <label className="mt-2 block text-xs text-muted-foreground">
          Date you received your MC#
          <input type="date" max={new Date().toISOString().slice(0, 10)} onChange={(e) => saveDate(e.target.value)} data-testid="new-entrant-mc-date"
            className="mt-1 block h-10 px-2 bg-[#141414] border border-[#262626] text-white text-sm [color-scheme:dark]" />
        </label>
      )}
      <a href={NEW_ENTRANT_URL} target="_blank" rel="noopener noreferrer" data-testid="new-entrant-link"
        className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#FFD600] hover:underline">
        <ExternalLink size={12} /> How to prepare (FMCSA)
      </a>
      <AuditPrepChecklist profile={profile} api={api} />
    </div>
  );
}
